import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t, type IntlService } from 'ember-intl';
import Heading from 'tio-ui/components/heading';
import {
  PhCheck,
  PhCircleDashed,
  PhSpinner,
  PhWarningCircle,
  PhCalendar,
  PhUser,
  PhArrowRight,
} from 'ember-phosphor-icons';
import { get } from '@ember/object';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TransitionLogSignature } from 'tio-common/types/tuition-assistance';

type StepStatus = 'completed' | 'active' | 'action-needed' | 'pending';

interface TimelineStep {
  titleKey: string;
  status: StepStatus;
  timestamp?: string;
  actorName?: string;
  actionHintKey?: string;
  isLast: boolean;
}

interface ApplicationStatusTrackerSignature {
  Args: {
    application: TasApplicationModel;
  };
  Element: HTMLDivElement;
}

const STEP_STYLES: Record<
  StepStatus,
  { node: string; line: string; title: string; detail: string }
> = {
  completed: {
    node: 'tio-bg-success text-white',
    line: 'tio-bg-success',
    title: 'tio-fg-success-h',
    detail: 'tio-fg-success',
  },
  active: {
    node: 'tio-bg-info text-white ring-4 ring-[var(--info-border)]',
    line: 'bg-gray-200',
    title: 'tio-fg-info-h font-semibold',
    detail: 'tio-fg-info-body',
  },
  'action-needed': {
    node: 'tio-bg-warning text-white ring-4 ring-[var(--warning-border)]',
    line: 'bg-gray-200',
    title: 'tio-fg-warning-h font-semibold',
    detail: 'tio-fg-warning',
  },
  pending: {
    node: 'bg-gray-200 tio-fg-disabled',
    line: 'bg-gray-200',
    title: 'tio-fg-disabled',
    detail: 'tio-fg-disabled',
  },
};

const DRAFT_STATES = ['TAS.ApplicationState.CREATED', 'TAS.ApplicationState.DEFINE_COURSES'];
const PENDING_APPROVAL_STATES = ['TAS.ApplicationState.PENDING_COURSES_APPROVAL'];
const APPROVED_STATES = ['TAS.ApplicationState.COURSES_APPROVED', 'TAS.ApplicationState.ATTEND'];
const PENDING_EVIDENCE_STATES = ['TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'];
const EVIDENCE_APPROVED_STATES = ['TAS.ApplicationState.EVIDENCE_APPROVED'];
const TERMINAL_STATES = ['TAS.ApplicationState.FULFILLED'];
const COURSE_REJECTION_STATES = [
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
];
const EVIDENCE_REJECTION_STATES = [
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
];

/**
 * ApplicationStatusTracker - Vertical timeline of the application lifecycle
 *
 * Shows steps from creation through completion, with real timestamps
 * and actor names from the application's transitionLog.
 *
 * @example
 * ```gts
 * <ApplicationStatusTracker @application={{this.application}} />
 * ```
 */
export default class ApplicationStatusTracker extends Component<ApplicationStatusTrackerSignature> {
  @service declare intl: IntlService;

  get application() {
    return this.args.application;
  }

  get state() {
    return this.application.state;
  }

  get transitionLog(): TransitionLogSignature[] {
    return this.application.transitionLog || [];
  }

  findLogEntry(event: string) {
    return this.transitionLog.find((entry) => entry.event === event);
  }

  formatDate(timestamp: string) {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return this.intl.formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  hasPassedState(states: string[]) {
    return this.transitionLog.some((entry) => states.includes(entry.from));
  }

  isInState(states: string[]) {
    return states.includes(this.state);
  }

  get steps(): TimelineStep[] {
    const raw: Omit<TimelineStep, 'isLast'>[] = [];

    // Step 1: Application Created (always completed if app exists)
    raw.push({
      titleKey: 'tuition_assistance.reimagined.status_tracker.step_started',
      status: 'completed',
      timestamp: this.application.createdAt
        ? this.formatDate(this.application.createdAt)
        : undefined,
    });

    // Step 2: Submitted for Approval
    const submitLog = this.findLogEntry('request_courses_approval');
    raw.push({
      titleKey: 'tuition_assistance.reimagined.status_tracker.step_submitted',
      status: this.hasPassedState(DRAFT_STATES)
        ? 'completed'
        : this.isInState(DRAFT_STATES)
          ? 'active'
          : 'pending',
      timestamp: submitLog ? this.formatDate(submitLog.timestamp) : undefined,
      actorName: submitLog?.reason?.by?.name,
    });

    // Step 3: Approval Review
    const approveLog = this.findLogEntry('approve_courses');
    const approvalStatus = ((): StepStatus => {
      if (this.isInState(COURSE_REJECTION_STATES)) return 'action-needed';
      if (this.hasPassedState(PENDING_APPROVAL_STATES)) return 'completed';
      if (this.isInState(PENDING_APPROVAL_STATES)) return 'active';
      return 'pending';
    })();
    raw.push({
      titleKey: 'tuition_assistance.reimagined.status_tracker.step_approval',
      status: approvalStatus,
      timestamp: approveLog ? this.formatDate(approveLog.timestamp) : undefined,
      actorName: approveLog?.reason?.by?.name,
      actionHintKey: this.isInState(COURSE_REJECTION_STATES)
        ? 'tuition_assistance.reimagined.status_tracker.action_fix_and_resubmit'
        : this.isInState(PENDING_APPROVAL_STATES)
          ? 'tuition_assistance.reimagined.status_tracker.action_waiting'
          : undefined,
    });

    // Step 4: Courses In Progress
    raw.push({
      titleKey: 'tuition_assistance.reimagined.status_tracker.step_in_progress',
      status: this.hasPassedState(APPROVED_STATES)
        ? 'completed'
        : this.isInState(APPROVED_STATES)
          ? 'active'
          : 'pending',
      actionHintKey: this.isInState(APPROVED_STATES)
        ? 'tuition_assistance.reimagined.status_tracker.action_complete_courses'
        : undefined,
    });

    // Step 5: Evidence Submitted
    const evidenceLog = this.findLogEntry('request_evidence_approval');
    raw.push({
      titleKey: 'tuition_assistance.reimagined.status_tracker.step_evidence',
      status: this.isInState(EVIDENCE_REJECTION_STATES)
        ? 'action-needed'
        : this.hasPassedState([...APPROVED_STATES, ...PENDING_EVIDENCE_STATES]) &&
            !this.isInState(APPROVED_STATES)
          ? 'completed'
          : this.isInState(APPROVED_STATES)
            ? 'pending'
            : 'pending',
      timestamp: evidenceLog ? this.formatDate(evidenceLog.timestamp) : undefined,
      actorName: evidenceLog?.reason?.by?.name,
      actionHintKey: this.isInState(APPROVED_STATES)
        ? 'tuition_assistance.reimagined.status_tracker.action_upload_evidence'
        : this.isInState(EVIDENCE_REJECTION_STATES)
          ? 'tuition_assistance.reimagined.status_tracker.action_fix_and_resubmit'
          : undefined,
    });

    // Step 6: Evidence Review
    const evidenceApproveLog = this.findLogEntry('approve_evidence');
    raw.push({
      titleKey: 'tuition_assistance.reimagined.status_tracker.step_evidence_review',
      status: this.isInState(EVIDENCE_REJECTION_STATES)
        ? 'action-needed'
        : this.hasPassedState(PENDING_EVIDENCE_STATES)
          ? 'completed'
          : this.isInState(PENDING_EVIDENCE_STATES)
            ? 'active'
            : 'pending',
      timestamp: evidenceApproveLog ? this.formatDate(evidenceApproveLog.timestamp) : undefined,
      actorName: evidenceApproveLog?.reason?.by?.name,
      actionHintKey: this.isInState(PENDING_EVIDENCE_STATES)
        ? 'tuition_assistance.reimagined.status_tracker.action_waiting'
        : undefined,
    });

    // Step 7: Complete
    const fulfillLog = this.findLogEntry('fulfill');
    raw.push({
      titleKey: 'tuition_assistance.reimagined.status_tracker.step_completed',
      status: this.isInState(TERMINAL_STATES)
        ? 'completed'
        : this.isInState(EVIDENCE_APPROVED_STATES)
          ? 'active'
          : 'pending',
      timestamp: fulfillLog ? this.formatDate(fulfillLog.timestamp) : undefined,
    });

    return raw.map((step, index) => ({
      ...step,
      isLast: index === raw.length - 1,
    }));
  }

  <template>
    <div class="rounded-2xl border tio-border-default tio-bg-surface p-6 shadow-sm" ...attributes>
      <Heading @level={{3}} class="mb-6">
        {{t "tuition_assistance.reimagined.status_tracker.title"}}
      </Heading>

      <div
        class="relative"
        role="list"
        aria-label={{t "tuition_assistance.reimagined.status_tracker.title"}}
      >
        {{#each this.steps as |step|}}
          <div class="relative tio-flex-loose {{if step.isLast 'pb-0' 'pb-8'}}" role="listitem">
            {{! Vertical connector line }}
            {{#unless step.isLast}}
              <div
                class="absolute left-4 top-8 h-full w-0.5 -translate-x-1/2
                  {{get (get STEP_STYLES step.status) 'line'}}"
              ></div>
            {{/unless}}

            {{! Step node }}
            <div
              class="relative z-10 tio-row-center h-8 w-8 shrink-0 rounded-full
                {{get (get STEP_STYLES step.status) 'node'}}"
            >
              {{#if (eq step.status "completed")}}
                <PhCheck @size={{16}} @weight="bold" />
              {{else if (eq step.status "active")}}
                <PhSpinner @size={{16}} class="animate-spin" />
              {{else if (eq step.status "action-needed")}}
                <PhWarningCircle @size={{16}} @weight="bold" />
              {{else}}
                <PhCircleDashed @size={{16}} />
              {{/if}}
            </div>

            {{! Step content }}
            <div class="tio-fill-min pt-0.5">
              <div class="text-sm {{get (get STEP_STYLES step.status) 'title'}}">
                {{t step.titleKey}}
              </div>

              {{#if step.timestamp}}
                <div
                  class="mt-1 tio-row-extra-tight text-xs
                    {{get (get STEP_STYLES step.status) 'detail'}}"
                >
                  <PhCalendar @size={{12}} />
                  <span>{{step.timestamp}}</span>
                </div>
              {{/if}}

              {{#if step.actorName}}
                <div
                  class="mt-0.5 tio-row-extra-tight text-xs
                    {{get (get STEP_STYLES step.status) 'detail'}}"
                >
                  <PhUser @size={{12}} />
                  <span>{{step.actorName}}</span>
                </div>
              {{/if}}

              {{#if step.actionHintKey}}
                <div
                  class="mt-2 tio-inline-row gap-1 rounded-lg px-3 py-1.5 tio-text-xs-medium
                    {{if
                      (eq step.status 'action-needed')
                      'tio-bg-warning-soft tio-fg-warning-body'
                      'tio-bg-info-soft tio-fg-info-body'
                    }}"
                >
                  <PhArrowRight @size={{12}} />
                  {{t step.actionHintKey}}
                </div>
              {{/if}}
            </div>
          </div>
        {{/each}}
      </div>
    </div>
  </template>
}
